import { Avatar, Flex } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import CopyableText from 'components/atoms/buttons/copyable-text';
import i18next from 'i18next';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';

import {
  PROGRAM_COLORS,
  formatDateCell,
  formatLanguage,
  getInitials,
} from '../utils/table-utils';
import styles from './styles.module.scss';

import type { ReactNode } from 'react';

export interface PatientCellRecord {
  guid: string;
  name: string;
  firstName: string;
  lastName: string;
  type?: string | null;
  birthDate?: string | null;
  language?: string | null;
}

interface PatientCellProps<RecordType extends PatientCellRecord> {
  record: RecordType;
  headingRightSlot?: ReactNode;
  headingWrapperClassName?: string;
}

export const renderPatientCell = <RecordType extends PatientCellRecord>({
  record,
  headingRightSlot,
  headingWrapperClassName,
}: PatientCellProps<RecordType>) => {
  const tags = record.type?.split('/')?.filter(Boolean) ?? [];
  const fullName = `${record.firstName} ${record.lastName}`;
  const initials = getInitials(fullName);
  const birthDateLabel = formatDateCell(record.birthDate);
  const languageLabel = formatLanguage(record.language);

  const heading = (
    <div className={styles['patient-heading']}>
      <Link to={Routes.patientGuid(record.guid)} className={styles.link}>
        {fullName}
      </Link>
      {!!tags.length && (
        <div className={styles['tag-group']}>
          {tags.map((tag) => {
            const palette = PROGRAM_COLORS[tag] ?? PROGRAM_COLORS.DEFAULT;
            return (
              <span
                key={`${record.guid}-${tag}`}
                className={styles.tag}
                style={{
                  backgroundColor: palette.bg,
                  color: palette.color,
                }}
              >
                {tag}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className={styles['patient-cell']}>
      <Flex className={styles['patient-avatar-wrapper']}>
        <Avatar className={styles.avatar}>{initials || 'PT'}</Avatar>
      </Flex>
      <div className={styles['patient-info']}>
        {heading}
        <div className={styles['meta-row']}>
          <CopyableText
            value={record.guid}
            label={i18next.t('controls.ptID')}
            icon={<CopyOutlined />}
          />
          <span className={styles['meta-divider']} aria-hidden="true" />
          <span className={styles['meta-value']}>{birthDateLabel}</span>
          <span className={styles['meta-divider']} aria-hidden="true" />
          <span className={styles['meta-value']}>{languageLabel}</span>
        </div>
        <div className={headingWrapperClassName}>{headingRightSlot}</div>
      </div>
    </div>
  );
};
